import {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
  useCallback,
  useRef,
} from "react";
import { wsconnect, NatsConnection, Status } from "@nats-io/nats-core";
import {
  compositeKey,
  Contract,
  contractExpired,
  ContractIdentifier,
  id,
  parse,
} from "./nats/contracts";

import {
  Consumer,
  ConsumerMessages,
  DeliverPolicy,
  jetstream,
  JetStreamClient,
} from "@nats-io/jetstream";
import { parseTopQuote, TopQuote, WireTopQuote } from "./nats/price";
import {
  parseVirtualTrade,
  tradeKey,
  VirtualTrade,
  WireVirtualTrade,
} from "./nats/virtualTrade";
import { getUserInitials, useGraphService } from "./GraphService";
import { Exchange } from "./ExchangeSelectorContextProvider";
import { getNatsUrl } from "../MFTEnvironmentVariables";

type Contracts = Generic<Contract>;
export type TopQuotes = Generic<TopQuote>;
/**
 * A tape, keyed by {@link tradeKey} rather than bucketed per area and contract
 * like the other two feeds — a contract takes many fills, so `contract_id` is
 * not an identity. See {@link consumeVirtualTrades}.
 */
type VirtualTrades = { [tradeKey: string]: VirtualTrade };

export enum NatsStatus {
  Uninitialized,
  Reconnecting,
  Connected,
  Disconnected,
}

const MAX_MESSAGES = 50000;
interface MessageContextType {
  contracts: Contracts;
  topQuotes: TopQuotes;
  /** Every fill in the stream's retention, deduped — see `consumeVirtualTrades`. */
  virtualTrades: VirtualTrades;
  status: NatsStatus;
}

type Generic<T> = {
  [key: string]: ContractDict<T>;
};

const defaultContextValue: MessageContextType = {
  contracts: {},
  topQuotes: {},
  virtualTrades: {},
  status: NatsStatus.Uninitialized,
};
export type ContractDict<T> = { [key: string]: T };

const MessageContext = createContext<MessageContextType>(defaultContextValue);

interface MessageProviderProps {
  children: ReactNode;
}

enum State {
  Open,
  Closed,
  Expired,
}

type ConsumerHandle = {
  messages: ConsumerMessages;
  consumer: Consumer;
};

const servers = getNatsUrl();

// `*` matches exactly one token and `>` the rest, so a single filter covers
// every JEPX area on all three streams. Contracts and quotes are bucketed by
// `delivery_area_id`, and trades carry theirs in the payload, so widening the
// filter is all it takes to hold all nine areas at once.
//
// Note a virtual-trades subject carries no area at all
// (`virtual_trades.jepx.<algo id hex>.<trade_id>`) — the area only exists in the
// payload's `contract.delivery_area_id`, as a bare digit.
const ALL_JEPX = "*.jepx.>";

export const MessageProvider = ({ children }: MessageProviderProps) => {
  const graphService = useGraphService();
  const [contracts, setContracts] = useState<Contracts>({});
  const [topQuotes, setTopQuotes] = useState<TopQuotes>({});
  const [virtualTrades, setVirtualTrades] = useState<VirtualTrades>({});
  const [status, setStatus] = useState<NatsStatus>(NatsStatus.Uninitialized);
  const [initials, setInitials] = useState("");

  const contractsIncomingRef = useRef<Contracts>({});
  const topQuotesIncomingRef = useRef<TopQuotes>({});
  const virtualTradesIncomingRef = useRef<VirtualTrades>({});
  // The trades store is mutated in place, so a snapshot is only published on the
  // ticks where something actually landed — see the publish loop below.
  const virtualTradesDirtyRef = useRef<boolean>(false);
  const contractStatesRef = useRef<Map<string, State>>(new Map());
  const connectionRef = useRef<NatsConnection | null>(null);
  const reconnectingRef = useRef<boolean>(false);

  const activeConsumersRef = useRef<Map<string, ConsumerHandle>>(new Map());

  const stopHandle = useCallback(async (key: string) => {
    const h = activeConsumersRef.current.get(key);
    if (!h) return;
    activeConsumersRef.current.delete(key);
    try {
      await h.messages.stop();
    } catch (err) {
      console.warn(`NATS: error stopping iterator for ${key}:`, err);
    }
    try {
      await h.consumer.delete();
    } catch (err) {
      console.warn(`NATS: error deleting consumer for ${key}:`, err);
    }
  }, []);

  const stopAllConsumers = useCallback(async () => {
    const keys = Array.from(activeConsumersRef.current.keys());
    await Promise.all(keys.map((k) => stopHandle(k)));
  }, [stopHandle]);

  const hasInitializedRef = useRef(false);

  const setStatusFromEvent = (eventType: Status) => {
    switch (eventType.type) {
      case "ldm":
      case "error":
      case "disconnect":
      case "reconnecting":
      case "staleConnection":
        setStatus(NatsStatus.Disconnected);
        break;
      case "update":
      case "reconnect":
      case "ping":
      case "forceReconnect":
        setStatus(NatsStatus.Connected);
        break;
    }
  };

  // Two consumers for the whole desk, created once. Previously this ran per
  // grid and only ever added, so clicking through the area tabs left a
  // consumer pair per visited area alive on the server until unmount.
  const startConsumers = useCallback(async (initials: string) => {
    const nc = connectionRef.current;
    if (!nc) {
      console.error("NATS: No connection available");
      return;
    }

    const pricesKey = `${initials}_ALL_PRICES`;
    const contractsKey = `${initials}_ALL_CONTRACTS`;
    const virtualTradesKey = `${initials}_ALL_VIRTUAL_TRADES`;

    if (
      activeConsumersRef.current.has(pricesKey) &&
      activeConsumersRef.current.has(contractsKey) &&
      activeConsumersRef.current.has(virtualTradesKey)
    ) {
      console.log("Consumers already running, reusing them");
      return;
    }

    console.log("NATS: Starting consumers for all areas");

    const js = jetstream(nc);

    if (!activeConsumersRef.current.has(pricesKey)) {
      const handle = await consumePrices(
        initials,
        js,
        topQuotesIncomingRef.current
      );
      activeConsumersRef.current.set(pricesKey, handle);
      console.log(`Started prices consumer: ${pricesKey}`);
    }

    if (!activeConsumersRef.current.has(contractsKey)) {
      const handle = await consumeContracts(
        initials,
        js,
        contractsIncomingRef.current,
        contractStatesRef.current
      );
      activeConsumersRef.current.set(contractsKey, handle);
      console.log(`Started contracts consumer: ${contractsKey}`);
    }

    if (!activeConsumersRef.current.has(virtualTradesKey)) {
      const handle = await consumeVirtualTrades(
        initials,
        js,
        virtualTradesIncomingRef.current,
        virtualTradesDirtyRef
      );
      activeConsumersRef.current.set(virtualTradesKey, handle);
      console.log(`Started virtual trades consumer: ${virtualTradesKey}`);
    }
  }, []);

  // NATS connection (once per initials)
  const nats_connect = useCallback(
    async (initials: string): Promise<NatsConnection | undefined> => {
      if (connectionRef.current) {
        console.log(`NATS: Connection already exists, reusing it`);
        return connectionRef.current;
      }

      console.log(`NATS: Creating new connection`);

      try {
        const nc = await wsconnect({
          servers,
          reconnect: true,
          maxReconnectAttempts: -1,
          reconnectTimeWait: 2000,
          timeout: 5000,
          name: `JEPX_LAZER_DESK-${initials}`,
          pingInterval: 10000,
          maxPingOut: 42,
        });

        connectionRef.current = nc;
        console.log(`NATS: Connected successfully`);
        setStatus(NatsStatus.Connected);

        const reconnect = async () => {
          if (reconnectingRef.current) {
            console.log("NATS: Reconnect already in progress, skipping");
            return;
          }

          reconnectingRef.current = true;
          console.log("NATS: Restarting consumers after reconnection");

          try {
            await stopAllConsumers();
            await startConsumers(initials);
          } finally {
            reconnectingRef.current = false;
          }
        };

        const monitorConnectionStatus = async () => {
          console.log("NATS: Starting connection status monitor");
          for await (const s of nc.status()) {
            if (s.type === "ping") continue;
            console.log(`NATS: status: ${s.type}`);
            setStatusFromEvent(s);

            if (s.type === "reconnect") {
              reconnect();
            }
          }
        };

        const handleConnectionClose = async () => {
          await nc.closed();
          await nc.close();

          console.warn(`NATS: Connection closed unexpectedly`);

          connectionRef.current = null;

          setStatus(NatsStatus.Disconnected);
          try {
            reconnect();
          } catch (err) {
            console.error(`NATS: Reconnection error:`, err);
          }
        };

        monitorConnectionStatus();
        handleConnectionClose();

        return nc;
      } catch (err) {
        console.error(`NATS: connection error:`, err);
        connectionRef.current = null;
        setStatus(NatsStatus.Disconnected);
        // yolo try again in 5 seconds, likely vpn just went down
        setTimeout(async () => {
          await stopAllConsumers();
          nats_connect(initials).then((nc) => {
            if (nc) {
              startConsumers(initials);
            }
          });
        }, 5000);
      }
    },
    [startConsumers, stopAllConsumers]
  );

  useEffect(() => {
    let lastUpdate = 0;
    let animationFrameId: number;

    const task = (timestamp: number) => {
      if (timestamp - lastUpdate >= 500) {
        const updated = getLiveAndDiscardExpired(
          contractsIncomingRef.current,
          contractStatesRef.current
        );
        setTopQuotes(topQuotesIncomingRef.current);
        // A fresh object, or React would see the same reference and bail out of
        // the render. `contracts` happens to hand it a new object every tick,
        // which is what used to drag the trades grid along with it.
        if (virtualTradesDirtyRef.current) {
          virtualTradesDirtyRef.current = false;
          setVirtualTrades({ ...virtualTradesIncomingRef.current });
        }
        setContracts(updated);
        lastUpdate = timestamp;
      }
      animationFrameId = requestAnimationFrame(task);
    };

    animationFrameId = requestAnimationFrame(task);
    return () => cancelAnimationFrame(animationFrameId);
  }, []);

  useEffect(() => {
    const updated = getUserInitials(graphService.user);
    if (initials != updated) {
      setInitials(updated);
    }
    //cries
    // eslint-disable-next-line
  }, [graphService]);

  // initially connects and starts consumers
  useEffect(() => {
    const contractsInc = contractsIncomingRef.current;
    const topQuotesInc = topQuotesIncomingRef.current;
    const virtualTradesInc = virtualTradesIncomingRef.current;
    if (initials && !hasInitializedRef.current) {
      hasInitializedRef.current = true;

      nats_connect(initials).then((nc) => {
        if (nc) {
          startConsumers(initials);
        }
      });
    }

    return () => {
      if (initials) {
        hasInitializedRef.current = false;
        Object.keys(contractsInc).forEach((k) => delete contractsInc[k]);
        Object.keys(topQuotesInc).forEach((k) => delete topQuotesInc[k]);
        Object.keys(virtualTradesInc).forEach(
          (k) => delete virtualTradesInc[k]
        );
        contractStatesRef.current = new Map<string, State>();
        stopAllConsumers();
      }
    };
  }, [initials, nats_connect, startConsumers, stopAllConsumers]);

  return (
    <MessageContext.Provider
      value={{ contracts, topQuotes, virtualTrades, status }}
    >
      {children}
    </MessageContext.Provider>
  );
};

/**
 * Prunes expired contracts from `items` in place and returns everything that is
 * still live — both {@link State.Open} and {@link State.Closed}.
 *
 * Closed contracts used to be withheld here, which made a contract the venue
 * suspends mid-session simply vanish from the order book: the trader saw the
 * neighbouring delivery windows and no indication the slot existed. Callers tell
 * the two apart with `Contract.open_for_trading`, which is on every object, so
 * the private {@link State} enum stays private.
 *
 * Expiry is decided at ingest (see `consumeContracts`), so a contract only
 * leaves on the venue's next republish of that subject — every few minutes on
 * JEPX.
 */
const getLiveAndDiscardExpired = <
  T extends { contract_identifier: ContractIdentifier },
>(
  items: Generic<T>,
  contractStatesMap: Map<string, State>
): Generic<T> => {
  const live: Generic<T> = {};
  let deletedCount = 0;
  const expiredKeys: string[] = [];

  for (const delivery_area_id in items) {
    const delivery_area = items[delivery_area_id];
    const live_delivery_area: ContractDict<T> = {};

    for (const contract_id in delivery_area) {
      const contract = delivery_area[contract_id];
      const key = compositeKey(contract.contract_identifier);
      const state = contractStatesMap.get(key);

      if (state === State.Open || state === State.Closed) {
        live_delivery_area[contract_id] = contract;
      } else if (state === State.Expired) {
        delete delivery_area[contract_id];
        expiredKeys.push(key);
        deletedCount++;
      }
    }

    if (Object.keys(live_delivery_area).length > 0) {
      live[delivery_area_id] = live_delivery_area;
    }
  }

  // After we delete expired contracts from the main data structure,
  // also delete them from the Map so it doesn't grow forever
  for (let i = 0; i < expiredKeys.length; i++) {
    contractStatesMap.delete(expiredKeys[i]);
  }

  if (deletedCount > 0) {
    console.log("Deleted", deletedCount, "expired contracts");
  }
  return live;
};

export const useMessages = (): MessageContextType => {
  return useContext(MessageContext);
};

const makeDrainLogger = (label: string) => {
  const started = Date.now();
  let received = 0;
  let skipped = 0;
  let backlog: number | undefined;
  let drained = false;

  return {
    skip: () => {
      skipped++;
    },
    tick: (pending: number) => {
      received++;

      if (backlog === undefined) {
        backlog = received + pending;
        console.log(`NATS ${label}: initial snapshot is ${backlog} messages`);
      }

      if (!drained && pending === 0) {
        drained = true;
        console.log(
          `NATS ${label}: snapshot drained in ${Date.now() - started}ms — ` +
            `${received} received, ${skipped} discarded as expired`
        );
      }
    },
  };
};

const consumeContracts = async (
  initials: string,
  js: JetStreamClient,
  contractsIncoming: Contracts,
  contractStatesMap: Map<string, State>
): Promise<ConsumerHandle> => {
  const consumerName = `${initials}_ALL_CONTRACTS_${Date.now()}`;

  const consumer = await js.consumers.get("CONTRACTS", {
    name_prefix: consumerName,
    filter_subjects: [ALL_JEPX],
    deliver_policy: DeliverPolicy.LastPerSubject,
  });

  const messages = await consumer.consume({
    max_messages: MAX_MESSAGES,
  });

  const drain = makeDrainLogger("CONTRACTS");

  const processContractMessages = async () => {
    try {
      for await (const m of messages) {
        drain.tick(m.info.pending);

        const exchange = m.subject.split(".")[1] as Exchange;

        const item: Contract = parse(m.string(), exchange);

        const key = id(item.contract_identifier);
        const grid_key = item.contract_identifier.delivery_area_id;
        const composite = compositeKey(item.contract_identifier);

        if (contractExpired(item)) {
          contractStatesMap.set(composite, State.Expired);
          drain.skip();
          continue;
        } else if (item.open_for_trading) {
          contractStatesMap.set(composite, State.Open);
        } else {
          contractStatesMap.set(composite, State.Closed);
        }

        if (!contractsIncoming[grid_key]) {
          contractsIncoming[grid_key] = {};
        }
        contractsIncoming[grid_key][key] = item;
      }
    } catch (err) {
      console.error("NATS: Contracts error:", err);
    }
  };

  processContractMessages();
  return { messages, consumer };
};
const consumePrices = async (
  initials: string,
  js: JetStreamClient,
  topQuotesIncoming: TopQuotes
): Promise<ConsumerHandle> => {
  const consumerName = `${initials}_ALL_PRICES_${Date.now()}`;

  const consumer = await js.consumers.get("PRICES", {
    name_prefix: consumerName,
    filter_subjects: [ALL_JEPX],
    deliver_policy: DeliverPolicy.LastPerSubject,
  });

  const messages = await consumer.consume({
    max_messages: MAX_MESSAGES,
  });

  const drain = makeDrainLogger("PRICES");

  const processPriceMessages = async () => {
    try {
      for await (const m of messages) {
        drain.tick(m.info.pending);

        const item = parseTopQuote(m.json<WireTopQuote>());

        const key = id(item.contract_identifier);
        const delivery_area_id = item.contract_identifier.delivery_area_id;

        if (!topQuotesIncoming[delivery_area_id]) {
          topQuotesIncoming[delivery_area_id] = {};
        }
        topQuotesIncoming[delivery_area_id][key] = item;
      }
    } catch (err) {
      console.error("NATS: Prices error:", err);
    }
  };

  processPriceMessages();
  return { messages, consumer };
};

/**
 * The desk's algo fills for as far back as the stream keeps them, which is a
 * `max_age` of seven days.
 *
 * This is the algo-side view of the same fills `PRIVATE_TRADES` carries: one
 * message per fill, published by the owning algo under
 * `virtual_trades.jepx.<algo id hex>.<trade_id>`, so it is the only feed that
 * says *which algo* traded. The stream is also configured for
 * `virtual_trade.>`, but nothing publishes to the singular form.
 *
 * Every trade is its own subject, so `LastPerSubject` would deliver all of them
 * too, but a self-trade is two algos publishing the venue's single `trade_id`
 * from their own subjects and neither subject carries the side. Hence `All`
 * here, and {@link tradeKey} rather than the id alone.
 *
 * Keying instead of appending is also what makes a reconnect safe. `reconnect`
 * starts a consumer under a new name, which replays the filter from the
 * beginning; a map absorbs that, an array would double every row. Ordered
 * consumers deliver in stream-sequence order, so where a venue republishes a
 * `trade_id` the last write is the current state.
 */
const consumeVirtualTrades = async (
  initials: string,
  js: JetStreamClient,
  virtualTradesIncoming: VirtualTrades,
  dirty: { current: boolean }
): Promise<ConsumerHandle> => {
  const consumerName = `${initials}_ALL_VIRTUAL_TRADES_${Date.now()}`;

  const consumer = await js.consumers.get("VIRTUAL_TRADES", {
    name_prefix: consumerName,
    filter_subjects: [ALL_JEPX],
    deliver_policy: DeliverPolicy.All,
  });

  const messages = await consumer.consume({
    max_messages: MAX_MESSAGES,
  });

  const drain = makeDrainLogger("VIRTUAL_TRADES");

  const processVirtualTradeMessages = async () => {
    try {
      for await (const m of messages) {
        drain.tick(m.info.pending);

        const item = parseVirtualTrade(m.json<WireVirtualTrade>());

        virtualTradesIncoming[tradeKey(item)] = item;
        dirty.current = true;
      }
    } catch (err) {
      console.error("NATS: Virtual trades error:", err);
    }
  };

  processVirtualTradeMessages();
  return { messages, consumer };
};
